/**
 * TableViewsBar.jsx - the views picker by a table's toolbar (10/9/26):
 * "View: My view •" opens the person's saved views (pick one to apply it),
 * with Save as new / Update / Rename / Delete / Set as my default / Share. A
 * change not saved yet shows a dot with Save and Revert beside the picker.
 * "Customize" opens the Customize panel (CustomizePanel.jsx). `children` sit at the
 * end (the Opportunities table puts its Export there).
 *
 * Round 4: views other people shared with this person are listed under
 * "Shared with me" ("Pipeline review · from Shawn Rimmell"), with a New badge
 * on the picker until the menu is opened. They are read-only here: apply,
 * "Save a copy as mine", open first, or hide (Show hidden brings them back).
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui';
import { Icon } from '../ui/Icon';
import CustomizePanel from './CustomizePanel';
import ShareViewDialog from './ShareViewDialog';

const ITEM = 'w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-2 flex items-center gap-2';
const INPUT = 'w-full border border-border-strong rounded px-2 py-1 text-[12px]';
const SECTION = 'px-3 pt-2 pb-1 text-[10px] uppercase tracking-[1px] font-bold text-text-muted';
const NEW_PILL = 'ml-1 px-1.5 rounded-full bg-gat-green text-white text-[9.5px] font-bold leading-[16px]';

/** "Shared with Paul Sarantos and 2 more" / "Shared with everyone" for an own view. */
function sharedLabel(v, nameOf) {
  const list = v.sharedWith || [];
  if (!list.length) return '';
  if (list.includes('*')) return 'Shared with everyone';
  const first = nameOf(list[0]);
  return list.length === 1 ? `Shared with ${first}` : `Shared with ${first} and ${list.length - 1} more`;
}

export default function TableViewsBar({ view, defs, children, hasGroups = false }) {
  const [open, setOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [sharing, setSharing] = useState(null); // the own view being shared
  const [naming, setNaming] = useState(null); // { mode: 'new' | 'rename', name, makeDefault }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        setNaming(null);
        setConfirmDelete(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const { active, views, modified } = view;
  const mine = view.mineViews || views;
  const shared = view.sharedViews || [];
  const hiddenCount = shared.filter((v) => v.hidden).length;
  const sharedShown = shared.filter((v) => showHidden || !v.hidden);
  const readOnly = !!(active && active.mine === false);
  const name = active ? active.name : 'Default';
  const defaultView = views.find((v) => v.isDefault) || null;
  const close = () => {
    setOpen(false);
    setNaming(null);
    setConfirmDelete(false);
  };
  const toggle = () => {
    if (!open) view.markSeen && view.markSeen();
    setOpen((o) => !o);
    setNaming(null);
    setConfirmDelete(false);
  };

  const submitName = async (e) => {
    e.preventDefault();
    const n = naming.name.trim();
    if (!n) return;
    const ok = naming.mode === 'new' ? await view.saveAs(n, naming.makeDefault) : await view.rename(active.id, n);
    if (ok) close();
  };

  return (
    <div className="flex items-center gap-2 flex-wrap ml-auto" ref={boxRef}>
      {view.enabled && (
        <div className="relative">
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 border border-border-strong rounded px-2.5 py-1.5 text-[11.5px] font-semibold bg-surface hover:bg-surface-2"
            aria-haspopup="menu"
            aria-expanded={open}
            title="Your saved views of this table"
          >
            <span className="text-text-muted font-bold uppercase text-[10px] tracking-[1px]">View:</span>
            <span data-testid="active-view-name">
              {name}
              {modified ? ' •' : ''}
            </span>
            {view.newCount > 0 && (
              <span className={NEW_PILL} data-testid="new-shared-views">
                {view.newCount} new
              </span>
            )}
            <span className="text-text-muted">▾</span>
          </button>
          {open && (
            <div
              role="menu"
              className="go-drop-in absolute right-0 top-full mt-1 z-30 w-80 bg-surface border border-border rounded-md shadow-lg py-1 max-h-[70vh] overflow-auto"
            >
              <button
                type="button"
                role="menuitem"
                className={ITEM}
                onClick={() => {
                  view.selectView(null);
                  close();
                }}
              >
                <span className="w-3">{!active ? '✓' : ''}</span> Default
                {!defaultView && <span className="ml-auto text-[10px] text-text-muted">opens first</span>}
              </button>
              {mine.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="menuitem"
                  className={ITEM}
                  onClick={() => {
                    view.selectView(v.id);
                    close();
                  }}
                  title={sharedLabel(v, (e) => e) || undefined}
                >
                  <span className="w-3">{active && active.id === v.id ? '✓' : ''}</span> {v.name}
                  {v.sharedWith && v.sharedWith.length > 0 && (
                    <span className="text-[10px] text-text-muted" aria-label="shared">
                      ⇪
                    </span>
                  )}
                  {v.isDefault && <span className="ml-auto text-[10px] text-text-muted">★ opens first</span>}
                </button>
              ))}
              {(sharedShown.length > 0 || hiddenCount > 0) && (
                <>
                  <div className={SECTION}>Shared with me</div>
                  {sharedShown.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      role="menuitem"
                      className={`${ITEM} ${v.hidden ? 'opacity-55' : ''}`}
                      onClick={() => {
                        view.selectView(v.id);
                        close();
                      }}
                    >
                      <span className="w-3">{active && active.id === v.id ? '✓' : ''}</span>
                      <span className="truncate">
                        {v.name} <span className="text-text-muted">· from {v.ownerName}</span>
                      </span>
                      {v.isNew && <span className={NEW_PILL}>New</span>}
                      {v.isDefault && <span className="ml-auto text-[10px] text-text-muted shrink-0">★</span>}
                    </button>
                  ))}
                  {hiddenCount > 0 && (
                    <button type="button" className={`${ITEM} text-text-muted`} onClick={() => setShowHidden((h) => !h)}>
                      <span className="w-3" />
                      {showHidden ? 'Leave hidden views out' : `Show hidden (${hiddenCount})`}
                    </button>
                  )}
                </>
              )}
              <div className="border-t border-border my-1" />
              {naming ? (
                <form onSubmit={submitName} className="px-3 py-2 space-y-2">
                  <input
                    autoFocus
                    aria-label="View name"
                    className={INPUT}
                    maxLength={60}
                    placeholder="View name"
                    value={naming.name}
                    onChange={(e) => setNaming({ ...naming, name: e.target.value })}
                  />
                  {naming.mode === 'new' && (
                    <label className="flex items-center gap-1.5 text-[11.5px]">
                      <input
                        type="checkbox"
                        checked={naming.makeDefault}
                        onChange={(e) => setNaming({ ...naming, makeDefault: e.target.checked })}
                      />
                      Open this view first
                    </label>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" type="submit">
                      {naming.mode === 'new' ? 'Save view' : 'Rename'}
                    </Button>
                    <Button size="sm" variant="ghost" type="button" onClick={() => setNaming(null)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className={ITEM}
                    onClick={() => setNaming({ mode: 'new', name: '', makeDefault: false })}
                  >
                    <span className="w-3" />
                    Save as new view…
                  </button>
                  {active && !readOnly && (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        disabled={!modified}
                        onClick={async () => {
                          if (await view.update()) close();
                        }}
                      >
                        <span className="w-3" />
                        <span className={modified ? '' : 'text-text-muted'}>Update “{active.name}”</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        onClick={() => {
                          setSharing(active);
                          close();
                        }}
                      >
                        <span className="w-3" />
                        Share…
                        {active.sharedWith && active.sharedWith.length > 0 && (
                          <span className="ml-auto text-[10px] text-text-muted">
                            {active.sharedWith.includes('*') ? 'everyone' : `${active.sharedWith.length} people`}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        onClick={() => setNaming({ mode: 'rename', name: active.name })}
                      >
                        <span className="w-3" />
                        Rename…
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        onClick={async () => {
                          await view.setDefault(active.isDefault ? null : active.id);
                        }}
                      >
                        <span className="w-3" />
                        {active.isDefault ? 'Stop opening this view first' : 'Set as my default'}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={`${ITEM} text-status-lost`}
                        onClick={async () => {
                          if (!confirmDelete) {
                            setConfirmDelete(true);
                            return;
                          }
                          if (await view.remove(active.id)) close();
                        }}
                      >
                        <span className="w-3" />
                        {confirmDelete ? `Click again to delete “${active.name}”` : 'Delete this view'}
                      </button>
                    </>
                  )}
                  {readOnly && (
                    <>
                      <div className="px-3 py-1 text-[11px] text-text-muted">
                        “{active.name}” is {active.ownerName}&rsquo;s view - read only.
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        onClick={async () => {
                          if (await view.copy(active.id)) close();
                        }}
                      >
                        <span className="w-3" />
                        Save a copy as mine
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        onClick={async () => {
                          await view.setDefault(active.isDefault ? null : active.id);
                        }}
                      >
                        <span className="w-3" />
                        {active.isDefault ? 'Stop opening this view first' : 'Set as my default'}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={ITEM}
                        onClick={async () => {
                          if (await view.hide(active.id, !active.hidden)) close();
                        }}
                      >
                        <span className="w-3" />
                        {active.hidden ? 'Show it in my list again' : 'Hide from my list'}
                      </button>
                    </>
                  )}
                  {!active && defaultView && (
                    <button
                      type="button"
                      role="menuitem"
                      className={ITEM}
                      onClick={async () => {
                        await view.setDefault(null);
                      }}
                    >
                      <span className="w-3" />
                      Set Default as my default
                    </button>
                  )}
                </>
              )}
              {view.error && (
                <div className="px-3 py-1.5 text-[11.5px] text-status-lost" role="alert">
                  {view.error}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {view.enabled && modified && (
        <>
          {active && !readOnly ? (
            <Button size="sm" variant="primary" onClick={() => view.update()}>
              Save
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setOpen(true);
                setNaming({ mode: 'new', name: '', makeDefault: false });
              }}
            >
              Save as view
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={view.revert}>
            Revert
          </Button>
        </>
      )}
      {view.enabled && !open && view.error && (
        <span className="text-[11.5px] text-status-lost" role="alert">
          {view.error}
        </span>
      )}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setCustomizing(true)}
        iconLeft={<Icon name="settings" size={12} color="currentColor" />}
      >
        Customize
      </Button>
      {children}
      {customizing && (
        <CustomizePanel
          view={view}
          defs={defs}
          hasGroups={hasGroups}
          onClose={() => setCustomizing(false)}
          onShare={(v) => setSharing(v)}
        />
      )}
      {sharing && (
        <ShareViewDialog view={view} target={views.find((v) => v.id === sharing.id) || sharing} onClose={() => setSharing(null)} />
      )}
    </div>
  );
}
